/**
 * @file companyRateFields.ts
 * @brief Shared config for per-company, per-product pricing overrides.
 *
 * @details
 * Companies can negotiate custom prices per product/currency. Each override
 * maps a `companies` minor-unit column (e.g. `custom_boardroom_rate_gbp_minor`)
 * to a major-unit form field (`..._major`) rendered in the Create/Edit dialogs.
 * Driving both dialogs off this single list keeps them in sync and mirrors the
 * four products on the Billing & Pricing panel exactly:
 *
 *   • Interpretation          → per minute + min charge
 *   • Realtime interpretation → per minute + min charge
 *   • Meeting Intel           → per minute + per speaker
 *   • Personal Notes          → per minute + min charge
 *
 * A blank input means "no override" → the platform system_settings rate for
 * that product/currency applies.
 *
 * @author DES
 * @date 06-07-2026
 */

import type { CreateCompanyInput } from "./schemas";

export type CurrencyCode = "usd" | "eur" | "gbp";
/** Form field name for a major-unit override input. */
export type MajorFieldName = `custom_${string}_major`;
/** Minor-unit payload/column key — only the numeric `custom_*_minor` overrides. */
export type MinorFieldName = Extract<keyof CreateCompanyInput, `custom_${string}_minor`>;

export interface CompanyRateField {
  /** Sub-field label within a product, e.g. "Per minute", "Min charge". */
  label: string;
  /** Builds the minor-unit `companies` column / payload key for a currency. */
  minorKey: (c: CurrencyCode) => MinorFieldName;
  /** Builds the major-unit form field name for a currency. */
  majorKey: (c: CurrencyCode) => MajorFieldName;
  /**
   * Builds the platform `system_settings` key that backs this field (the
   * "general" rate set on the Billing & Pricing page). Used as the input
   * placeholder so admins see the default a blank field inherits.
   */
  settingKey: (c: CurrencyCode) => string;
}

export interface CompanyRateProduct {
  /** Product name — mirrors the Billing & Pricing panel cards. */
  product: string;
  fields: CompanyRateField[];
}

export const CURRENCY_CODES: CurrencyCode[] = ["usd", "eur", "gbp"];

export const COMPANY_RATE_PRODUCTS: CompanyRateProduct[] = [
  {
    product: "Interpretation",
    fields: [
      {
        label: "Per minute",
        minorKey: (c) => `custom_rate_${c}_minor`,
        majorKey: (c) => `custom_rate_${c}_major`,
        settingKey: (c) => `RATE_PER_MINUTE_${c.toUpperCase()}_MINOR`,
      },
      {
        label: "Min charge",
        minorKey: (c) => `custom_min_${c}_minor`,
        majorKey: (c) => `custom_min_${c}_major`,
        settingKey: (c) => `MIN_CHARGE_${c.toUpperCase()}_MINOR`,
      },
    ],
  },
  {
    product: "Realtime interpretation",
    fields: [
      {
        label: "Per minute",
        minorKey: (c) => `custom_realtime_rate_${c}_minor`,
        majorKey: (c) => `custom_realtime_rate_${c}_major`,
        settingKey: (c) => `RATE_REALTIME_PER_MINUTE_${c.toUpperCase()}_MINOR`,
      },
      {
        label: "Min charge",
        minorKey: (c) => `custom_realtime_min_${c}_minor`,
        majorKey: (c) => `custom_realtime_min_${c}_major`,
        settingKey: (c) => `MIN_CHARGE_REALTIME_${c.toUpperCase()}_MINOR`,
      },
    ],
  },
  {
    product: "Meeting Intel",
    fields: [
      {
        label: "Per minute",
        minorKey: (c) => `custom_boardroom_rate_${c}_minor`,
        majorKey: (c) => `custom_boardroom_rate_${c}_major`,
        settingKey: (c) => `RATE_BOARDROOM_PER_MINUTE_${c.toUpperCase()}_MINOR`,
      },
      {
        label: "Per speaker",
        minorKey: (c) => `custom_boardroom_speaker_fee_${c}_minor`,
        majorKey: (c) => `custom_boardroom_speaker_fee_${c}_major`,
        settingKey: (c) => `BOARDROOM_SPEAKER_FEE_${c.toUpperCase()}_MINOR`,
      },
    ],
  },
  {
    product: "Personal Notes",
    fields: [
      {
        label: "Per minute",
        minorKey: (c) => `custom_note_rate_${c}_minor`,
        majorKey: (c) => `custom_note_rate_${c}_major`,
        settingKey: (c) => `RATE_NOTE_PER_MINUTE_${c.toUpperCase()}_MINOR`,
      },
      {
        label: "Min charge",
        minorKey: (c) => `custom_note_min_${c}_minor`,
        majorKey: (c) => `custom_note_min_${c}_major`,
        settingKey: (c) => `MIN_CHARGE_NOTE_${c.toUpperCase()}_MINOR`,
      },
    ],
  },
];

/** Flat list of every (field × currency) override — handy for reset/diff loops. */
export const ALL_COMPANY_RATE_FIELDS: CompanyRateField[] =
  COMPANY_RATE_PRODUCTS.flatMap((p) => p.fields);
